import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { useLoad } from '../hooks';
import { Card, ErrorNote, Loading, Table } from '../components/ui';
import type { PlatformHealth, VisionModelList, WorkspaceSettings } from '../types';

const MODES: Array<{ value: WorkspaceSettings['imageAnalysisMode']; label: string; hint: string }> = [
  { value: 'OFF', label: 'Kapalı', hint: 'Hiçbir görsel işlenmez.' },
  { value: 'OCR', label: 'Yalnız OCR', hint: 'Sunucuda çalışır, ek maliyeti yoktur.' },
  { value: 'AI', label: 'Yalnız yapay zekâ', hint: 'Dış servise ücret ödenir.' },
  { value: 'OCR_AI', label: 'OCR + yapay zekâ', hint: 'Her görsel iki kez işlenir.' },
];

/** Operatörün kim olduğunu ve hangi platforma baktığını doğruladığı yer. */
export function Settings() {
  const { session, switchWorkspace, logout } = useAuth();
  const health = useLoad(() => api.get<PlatformHealth>('/health'));
  const settings = useLoad(() => api.get<WorkspaceSettings>('/v1/workspace/settings'));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function save(patch: Partial<WorkspaceSettings>) {
    setSaving(true);
    setSaveError(null);
    try {
      await api.patch<WorkspaceSettings>('/v1/workspace/settings', patch);
      settings.reload();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Ayar kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  }

  if (!session) return null;

  const ROLE_TR: Record<string, string> = {
    OWNER: 'sahip',
    ADMIN: 'yönetici',
    MEMBER: 'üye',
    VIEWER: 'izleyici',
  };

  return (
    <>
      <Card title="Oturum">
        <dl className="kv" style={{ padding: 0 }}>
          <dt>Kullanıcı</dt>
          <dd className="cell-title">{session.user.displayName ?? session.user.email}</dd>
          <dt>E-posta</dt>
          <dd className="muted">{session.user.email}</dd>
          <dt>Çalışma alanı</dt>
          <dd>
            <select
              value={session.workspaceId}
              onChange={(e) => switchWorkspace(e.target.value)}
              style={{ maxWidth: 320 }}
            >
              {session.workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name} ({ROLE_TR[workspace.role] ?? workspace.role})
                </option>
              ))}
            </select>
          </dd>
        </dl>
        <div style={{ marginTop: 16 }}>
          <button type="button" className="ghost danger" onClick={logout}>
            Çıkış yap
          </button>
        </div>
      </Card>

      <Card title="Görsel işleme">
        <ErrorNote message={settings.error ?? saveError} />
        {settings.loading && !settings.data ? (
          <Loading />
        ) : settings.data ? (
          <>
            <div className="field">
              <span className="field-label">Mod</span>
              <span className="field-hint">
                Yapay zekâ seçenekleri dış bir servise ücret ödetir; işlenen her yeni
                görsel için ödeme yapılır. Daha önce işlenmiş bir görsel tekrar
                işlenmez.
              </span>
              <select
                value={settings.data.imageAnalysisMode}
                disabled={saving}
                onChange={(e) =>
                  void save({
                    imageAnalysisMode: e.target
                      .value as WorkspaceSettings['imageAnalysisMode'],
                  })
                }
                style={{ maxWidth: 320 }}
              >
                {MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label} — {mode.hint}
                  </option>
                ))}
              </select>
            </div>

            {(settings.data.imageAnalysisMode === 'AI' ||
              settings.data.imageAnalysisMode === 'OCR_AI') && (
              <>
                <ModelField
                  value={settings.data.visionModel}
                  disabled={saving}
                  onSave={(visionModel) => void save({ visionModel })}
                />
                <PromptField
                  value={settings.data.visionPrompt}
                  disabled={saving}
                  onSave={(visionPrompt) => void save({ visionPrompt })}
                />
              </>
            )}
          </>
        ) : null}
      </Card>

      <Card
        title="Platform"
        flush
        footer={health.data ? `${health.data.service} · sürüm ${health.data.version}` : undefined}
      >
        {health.error && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={health.error} />
          </div>
        )}
        {health.loading && !health.data && <Loading />}
        {health.data && (
          <Table head={['Bileşen', 'Durum', 'Gecikme', 'Hata']}>
            <tr>
              <td className="cell-title">API</td>
              <td>{health.data.status === 'ok' ? 'Ayakta' : 'Sorunlu'}</td>
              <td className="muted">—</td>
              <td className="muted">—</td>
            </tr>
            {Object.entries(health.data.checks).map(([name, check]) => (
              <tr key={name}>
                <td className="cell-title">
                  {name === 'database' ? 'Veritabanı' : name === 'redis' ? 'Redis' : name}
                </td>
                <td>{check.status === 'ok' ? 'Ayakta' : 'Sorunlu'}</td>
                <td className="muted">{check.latencyMs ? `${check.latencyMs} ms` : '—'}</td>
                <td className="muted">{check.error ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

/**
 * Model bir listeden seçiliyor, elle yazılmıyor.
 *
 * Önce serbest metindi ve gerekçesi "yeni model çıkınca yeniden dağıtım beklemeyelim"di.
 * Pratikte ödediği bedel daha ağır çıktı: yanlış yazılan bir ad kaydedilebiliyor ve ancak
 * ilk çağrıda — yani bir tarama başarısız olduğunda — anlaşılıyordu.
 *
 * Liste sunucudan geliyor ve kaynağı fiyat tablosu. Yani seçilebilir olmak ile harcaması
 * hesaplanabilir olmak aynı şey; fiyatı bilinmeyen bir model seçilip toplam harcamayı
 * sessizce eksik bırakamıyor. Fiyat da yanında gösteriliyor — seçim yapan kişi ne
 * ödeyeceğini görüyor.
 *
 * Model değiştirmek eski sonuçları geçersiz KILMAZ: model kimliği önbellek anahtarının
 * parçası, yani yeni model kendi önbelleğini doldurur ve eskisi okunabilir kalır.
 */
function ModelField({
  value,
  disabled,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (model: string | null) => void;
}) {
  const models = useLoad(() => api.get<VisionModelList>('/v1/workspace/vision-models'));

  return (
    <div className="field">
      <span className="field-label">Model</span>
      <span className="field-hint">
        Fiyatlar milyon token başına. Bir 1080×1350 afiş yaklaşık 1.000 girdi tokenı
        harcıyor. Model değiştirmek daha önce işlenmiş görselleri etkilemez.
      </span>
      <ErrorNote message={models.error} />
      <select
        value={value ?? ''}
        disabled={disabled || models.loading}
        style={{ maxWidth: 420 }}
        onChange={(e) => onSave(e.target.value === '' ? null : e.target.value)}
      >
        <option value="">sunucu varsayılanı</option>
        {(models.data?.items ?? []).map((model) => (
          <option key={model.id} value={model.id}>
            {model.id} — ${model.inputPerMillion} girdi / ${model.outputPerMillion} çıktı
          </option>
        ))}
      </select>
      {models.data && (
        <span className="field-hint">
          Fiyatlar {models.data.pricesUpdatedAt} tarihli liste fiyatlarına göre.
        </span>
      )}
    </div>
  );
}

/**
 * İstem panelde, çünkü en sık ayarlanan şey bu: bir kuralın eksik olduğu her fark
 * edildiğinde yeniden dağıtım beklemek düzeltmeyi geciktirirdi.
 *
 * Uzunluğu gösteriliyor, çünkü her karakteri her çağrıda ücretli — istem şiştikçe fatura
 * da şişer.
 */
function PromptField({
  value,
  disabled,
  onSave,
}: {
  value: string | null;
  disabled: boolean;
  onSave: (prompt: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const changed = draft.trim() !== (value ?? '');

  return (
    <div className="field">
      <span className="field-label">İstem</span>
      <span className="field-hint">
        Modele aynen gönderilir; Rab-bit içeriğini yorumlamaz. Boş bırakılırsa sunucudaki
        varsayılan kullanılır. Her karakteri her çağrıda ücretlidir.
      </span>
      <span className="field-hint">
        <code>{'{{context.anahtar}}'}</code> yazarak actor'ın o görsel hakkında bildirdiği
        olgulara referans verebilirsin — karşılığı her görselde ayrı doldurulur.
        Instagram taramasında <code>{'{{context.publishedAt}}'}</code> gönderinin paylaşım
        tarihini verir; afişte yıl yazmadığında yılı ancak bu çözer. Karşılığı olmayan bir
        anahtar olduğu gibi kalır, böylece yanlış yazıldığı çıktıda görünür.
      </span>
      <textarea
        rows={10}
        value={draft}
        disabled={disabled}
        placeholder="sunucu varsayılanı"
        onChange={(e) => setDraft(e.target.value)}
        style={{ fontFamily: 'var(--mono, monospace)', fontSize: 12 }}
      />
      <div className="field-row" style={{ marginTop: 8, alignItems: 'center' }}>
        <span className="muted small">{draft.length} karakter</span>
        <button
          type="button"
          disabled={disabled || !changed}
          onClick={() => onSave(draft.trim() === '' ? null : draft.trim())}
        >
          İstemi kaydet
        </button>
      </div>
    </div>
  );
}
